import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DeviceMobile, WhatsappLogo, ArrowLeft, ShieldCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, WHATSAPP_NUMBER, PAGO_MOVIL } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Checkout() {
  const { payItems: items, consultItems, total } = useCart();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", payment_reference: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const confirmStockUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hola Ciudad Garaje, quiero confirmar la disponibilidad de mi pedido antes de pagar:\n" +
      items.map((i) => `• ${i.quantity}x ${i.name}`).join("\n")
  )}`;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-28 text-center">
        <h1 className="font-display text-4xl">No hay nada para pagar</h1>
        <Link to="/catalogo" className="inline-block mt-6 text-[#D97736] uppercase tracking-widest text-sm">Ir al catálogo</Link>
      </div>
    );
  }

  const validate = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Ingresa tu nombre y apellido");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Ingresa un correo válido");
      return false;
    }
    if (form.phone.trim().length < 7) {
      toast.error("Ingresa un teléfono válido");
      return false;
    }
    return true;
  };

  const confirmPagoMovil = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        items: items.map((i) => ({ beer_id: i.id, quantity: i.quantity, size: i.size || null })),
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        method: "pago_movil",
        payment_reference: form.payment_reference,
      });
      nav(`/gracias?order=${res.data.id}&t=${res.data.token}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo crear el pedido");
      setLoading(false);
    }
  };

  const orderViaWhatsapp = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        items: items.map((i) => ({ beer_id: i.id, quantity: i.quantity, size: i.size || null })),
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        method: "whatsapp",
      });
      nav(`/gracias?order=${res.data.id}&t=${res.data.token}`);
    } catch (e) {
      toast.error("No se pudo crear el pedido");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
      <Link to="/carrito" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#D97736] text-sm uppercase tracking-widest mb-8 transition-colors">
        <ArrowLeft size={16} /> Volver al carrito
      </Link>
      <h1 className="font-display text-5xl md:text-6xl mb-10">Finalizar compra</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-10">
          {/* Datos del cliente */}
          <div>
            <div className="overline mb-5">Tus datos</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Nombre</label>
                <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="Tu nombre" data-testid="checkout-first-name" />
              </div>
              <div>
                <label className="overline block mb-2">Apellido</label>
                <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="Tu apellido" data-testid="checkout-last-name" />
              </div>
              <div>
                <label className="overline block mb-2">Correo</label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="tucorreo@ejemplo.com" data-testid="checkout-email" />
              </div>
              <div>
                <label className="overline block mb-2">Teléfono</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="0414 000 0000" data-testid="checkout-phone" />
              </div>
            </div>
          </div>

          {/* Pago */}
          <div>
            <div className="overline mb-5">Método de pago</div>

            {/* Aviso: confirmar disponibilidad por WhatsApp */}
            <div className="mb-5 border-l-4 border-[#F5A623] bg-[#F5A623]/10 p-4" data-testid="stock-notice">
              <div className="flex items-start gap-3">
                <Warning size={22} weight="fill" className="text-[#F5A623] shrink-0 mt-0.5" />
                <div className="text-sm text-gray-200 leading-relaxed">
                  <span className="font-semibold text-[#F5A623]">Antes de pagar, confirma la disponibilidad por WhatsApp.</span>{" "}
                  Nuestras cervezas son <span className="text-white">artesanales de baja producción</span> y se agotan rápido. Escríbenos para asegurar tu pedido antes de realizar el pago.
                  <a
                    href={confirmStockUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold px-3 py-2.5 uppercase tracking-wide text-xs transition-colors"
                    data-testid="confirm-stock-whatsapp"
                  >
                    <WhatsappLogo size={16} weight="fill" /> Confirmar disponibilidad
                  </a>
                </div>
              </div>
            </div>

            {/* Pago Móvil */}
            <div className="border border-[#D97736]/40 bg-[#141414] p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 border border-[#D97736] flex items-center justify-center text-[#D97736]"><DeviceMobile size={20} weight="bold" /></div>
                <div>
                  <div className="font-display text-2xl leading-none">Pago Móvil</div>
                  <div className="overline">Pago en Venezuela</div>
                </div>
              </div>
              <div className="space-y-2 text-sm bg-[#0d0d0d] border border-white/10 p-4">
                <div className="flex justify-between"><span className="text-gray-500">RIF</span><span className="text-white font-medium">{PAGO_MOVIL.rif}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Teléfono</span><span className="text-white font-medium">{PAGO_MOVIL.telefono}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Banco</span><span className="text-white font-medium">{PAGO_MOVIL.banco}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Correo</span><span className="text-white font-medium break-all">{PAGO_MOVIL.email}</span></div>
              </div>
              <p className="text-gray-500 text-xs mt-3">Realiza el Pago Móvil con los datos de arriba y, si ya lo hiciste, escribe abajo el número de referencia de tu comprobante.</p>
              <div className="mt-4">
                <label className="overline block mb-2">Número de comprobante / referencia (opcional)</label>
                <input
                  value={form.payment_reference}
                  onChange={(e) => set("payment_reference", e.target.value)}
                  className="w-full bg-[#0d0d0d] border border-white/15 focus:border-[#D97736] outline-none py-3 px-3 text-white"
                  placeholder="Ej: 001234567"
                  data-testid="checkout-payment-reference"
                />
              </div>
              <button
                onClick={confirmPagoMovil}
                disabled={loading}
                className="w-full mt-4 flex items-center justify-center gap-2 bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-60 text-black font-semibold uppercase tracking-wide py-4 transition-colors"
                data-testid="confirm-pagomovil-btn"
              >
                <DeviceMobile size={20} weight="bold" /> {loading ? "Procesando..." : "Confirmar pedido con Pago Móvil"}
              </button>
            </div>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/10" />
              <span className="overline">o</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <button
              onClick={orderViaWhatsapp}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 disabled:opacity-60 uppercase tracking-wide px-6 py-4 transition-colors"
              data-testid="order-whatsapp-btn"
            >
              <WhatsappLogo size={22} weight="fill" /> Pedir por WhatsApp
            </button>

            <p className="text-gray-500 text-xs mt-4 flex items-center gap-2">
              <ShieldCheck size={14} /> Tu pedido queda registrado. Confirmamos al recibir el comprobante de pago.
            </p>
          </div>
        </div>

        {/* Resumen */}
        <div className="h-fit bg-[#171717] border border-white/10 p-6 lg:sticky lg:top-28" data-testid="checkout-summary">
          <div className="overline mb-5">Tu pedido</div>
          <div className="space-y-4 max-h-80 overflow-auto cg-scroll pr-1">
            {items.map((it) => (
              <div key={it.key || it.id} className="flex gap-3 items-center">
                <div className="relative">
                  <img src={it.image_url} alt={it.name} className="w-14 h-16 object-cover border border-white/10" />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#D97736] text-black text-xs flex items-center justify-center font-semibold">{it.quantity}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="overline">{it.style}</div>
                </div>
                <div className="font-display text-xl">{money(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 mt-6 pt-5 flex justify-between items-end">
            <span className="overline">Total</span>
            <span className="font-display text-4xl text-[#D97736]">{money(total)}</span>
          </div>
          {consultItems.length > 0 && (
            <p className="mt-4 text-xs text-[#25D366]" data-testid="checkout-consult-note">
              Tus {consultItems.length} cerveza(s) importada(s) no se cobran aquí: su precio se consulta aparte por WhatsApp desde el carrito.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
